'use client';

import { cn } from '@/shared/utils/formatting';
import { AlertCircle, Eye, EyeOff, KeyRound, Lock } from 'lucide-react';
import { forwardRef, useId, useState, type InputHTMLAttributes } from 'react';

type Icon = 'lock' | 'key' | 'none';

export interface UnderlineFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'className' | 'id'
> {
  label: string;
  /** `password` adds the show / hide toggle. */
  type?: 'text' | 'password';
  icon?: Icon;
  /** Monospace, uppercase, wide tracking — recovery codes. */
  mono?: boolean;
  error?: string | undefined;
  hint?: string | undefined;
  id?: string;
}

const ICONS: Record<Exclude<Icon, 'none'>, typeof Lock> = { lock: Lock, key: KeyRound };

function FieldFoot({ error, hint }: { error?: string | undefined; hint?: string | undefined }) {
  if (error) {
    return (
      <p role="alert" className="flex items-center gap-2 text-[13px] text-red-primary">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {error}
      </p>
    );
  }
  if (hint) return <p className="text-[12.5px] leading-[1.5] text-text-tertiary">{hint}</p>;
  return null;
}

/**
 * The underline input from the Choose Plan visual language: eyebrow label,
 * 50px row, 2px bottom border that turns red on focus, red + shake on error.
 * `components/ui/Input` is the boxed variant and cannot be coerced into this.
 */
export const UnderlineField = forwardRef<HTMLInputElement, UnderlineFieldProps>(
  function UnderlineField(
    { label, type = 'text', icon = 'none', mono = false, error, hint, id, disabled, ...props },
    ref
  ) {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const [show, setShow] = useState(false);
    const IconComponent = icon === 'none' ? null : ICONS[icon];

    return (
      <div className="flex flex-col gap-3">
        <label
          htmlFor={inputId}
          className="text-[11px] font-semibold tracking-[0.12em] uppercase text-text-tertiary"
        >
          {label}
        </label>
        <div
          className={cn(
            'flex items-center gap-2.5 h-[50px] border-b-2 border-white/[0.16] transition-colors focus-within:border-red-primary',
            error && 'border-red-primary animate-[cp-shake_0.4s_ease]',
            disabled && 'opacity-60'
          )}
        >
          {IconComponent ? (
            <IconComponent className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
          ) : null}
          <input
            ref={ref}
            id={inputId}
            type={type === 'password' && !show ? 'password' : 'text'}
            disabled={disabled}
            aria-invalid={!!error}
            className={cn(
              'flex-1 min-w-0 h-full bg-transparent border-0 outline-none text-text-primary text-[17px] font-medium placeholder:text-text-tertiary/50 placeholder:font-normal',
              'focus-visible:outline-0! focus-visible:outline-offset-0!',
              mono && 'font-mono text-[19px] tracking-[0.16em] uppercase'
            )}
            {...props}
          />
          {type === 'password' ? (
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              tabIndex={-1}
              aria-label={show ? 'Hide password' : 'Show password'}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[2px] text-text-tertiary transition-colors hover:bg-white/[0.04] hover:text-text-primary"
            >
              {show ? (
                <EyeOff className="h-[17px] w-[17px]" aria-hidden />
              ) : (
                <Eye className="h-[17px] w-[17px]" aria-hidden />
              )}
            </button>
          ) : null}
        </div>
        <FieldFoot error={error} hint={hint} />
      </div>
    );
  }
);
